import { useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Send } from "lucide-react";
import { SIGNALS } from "@/data/signals";
import { SignalRow } from "@/components/home/LatestSignals";
import { Eyebrow } from "@/components/shared/Primitives";
import { Reveal, SplitLines } from "@/components/motion/Reveal";

const GROUPS = ["All", "Forex", "Metals", "Indices"];
const STATUSES = ["All", "Active", "Pending", "Target reached", "Closed", "Stopped"];
const DIRS = ["All", "Long", "Short"];
const DATES = [["All", 0], ["24h", 1], ["7 days", 7], ["30 days", 30]];
const NOW = new Date("2026-06-12T12:00:00Z");

const Seg = ({ options, value, onChange, testid }) => (
  <div className="seg flex-wrap" data-testid={testid}>{options.map((o) => { const [label, val] = Array.isArray(o) ? o : [o, o]; return <button key={label} data-on={value === val} onClick={() => onChange(val)} data-testid={`${testid}-${label.toLowerCase().replace(/\s/g, "-")}`}>{label}</button>; })}</div>
);

export default function Signals() {
  const [group, setGroup] = useState("All");
  const [status, setStatus] = useState("All");
  const [dir, setDir] = useState("All");
  const [range, setRange] = useState(0);
  const list = useMemo(() => SIGNALS.filter((s) => (group === "All" || s.group === group) && (status === "All" || s.status === status) && (dir === "All" || s.direction === dir) && (!range || (NOW - new Date(s.publishedAt)) / 86400000 <= range)), [group, status, dir, range]);
  const closed = SIGNALS.filter((s) => s.outcome);
  const wins = closed.filter((s) => s.outcome.result === "Win").length;

  return (
    <div className="container-x py-12 lg:py-16" data-testid="signals-page">
      <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
        <div className="lg:col-span-8">
          <Eyebrow className="mb-4">Trading signals</Eyebrow>
          <SplitLines lines={["Market intelligence.", "Clear trading ideas."]} className="t-hero" />
          <p className="mt-6 max-w-xl t-lead">Research-grade trade ideas with explicit entry conditions, invalidation and targets. Originals are preserved; updates are appended, never rewritten.</p>
        </div>
        <Reveal delay={0.2} className="lg:col-span-4 lg:pt-10">
          <div className="card card-dark p-6 text-white">
            <p className="font-mono text-[11px] tracking-[0.04em] text-white/50">Track record · this prototype</p>
            <div className="mt-4 grid grid-cols-3 gap-4">
              <div><p className="t-stat text-white">{SIGNALS.length}</p><p className="t-stat-label text-white/50">Ideas published</p></div>
              <div><p className="t-stat text-white">{wins}/{closed.length}</p><p className="t-stat-label text-white/50">Closed wins</p></div>
              <div><p className="t-stat text-white">+4.0R</p><p className="t-stat-label text-white/50">Net closed</p></div>
            </div>
            <a href="https://t.me/forexbrokersignals" target="_blank" rel="noreferrer" className="btn mt-6 h-11 w-full bg-white text-[var(--ink)] hover:bg-[#e8eefc]" data-testid="signals-telegram-cta"><Send size={15} /> Follow our signals on Telegram</a>
          </div>
        </Reveal>
      </div>

      <Reveal delay={0.1} className="mt-12 flex flex-wrap items-center gap-3" data-testid="signals-filters">
        <Seg options={GROUPS} value={group} onChange={setGroup} testid="signals-filter-group" />
        <Seg options={DIRS} value={dir} onChange={setDir} testid="signals-filter-direction" />
        <Seg options={STATUSES} value={status} onChange={setStatus} testid="signals-filter-status" />
        <Seg options={DATES} value={range} onChange={setRange} testid="signals-filter-date" />
      </Reveal>

      <div className="mt-6 border-t border-[var(--line)]">
        <AnimatePresence initial={false}>
          {list.map((s) => <motion.div key={s.id} layout initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}><SignalRow s={s} /></motion.div>)}
        </AnimatePresence>
        {list.length === 0 && <p className="py-16 text-center text-sm text-[var(--ink-3)]" data-testid="signals-empty">No ideas match those filters.</p>}
      </div>
      <p className="mt-8 max-w-3xl text-xs leading-relaxed text-[var(--ink-4)]">Trading ideas are published for research purposes and do not constitute personal investment advice. Past performance is not indicative of future results. A fully auditable performance dashboard is planned for a later phase.</p>
    </div>
  );
}
